import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiFileImage, mdiFileChart } from '@mdi/js'

import { $api } from '../../api/queryClient'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Badge } from '../ui/badge'
import { Checkbox } from '../ui/checkbox'
import type { components } from '../../api/schema'

type RepresentationCandidate = components['schemas']['RepresentationCandidate']

interface Choice {
    candidate: RepresentationCandidate
    assetUid: string
    skip: boolean
}

interface Props {
    org: string
    defaultAssetUid: string
    documentUid: string
    candidates: RepresentationCandidate[]
    onCancel: () => void
    onConfirm: (choices: Choice[]) => Promise<void>
}

export function PlanCandidateWizard({ org, defaultAssetUid, documentUid: _documentUid, candidates, onCancel, onConfirm }: Props) {
    const { t } = useTranslation('assets')
    const [submitting, setSubmitting] = useState(false)

    // Fetch the default asset's parent + siblings + children for the picker
    // For simplicity v1: fetch the parent's children if available, else the asset's children
    const { data: assetResp } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
        { params: { path: { orgCode: org, assetUid: defaultAssetUid } } },
    )
    const asset = assetResp as { uid?: string; parent_asset_uid?: string; name?: string } | undefined

    const parentUid = asset?.parent_asset_uid

    // Pull a flat list of candidate assets: parent's children (siblings + self), plus current asset's descendants
    const { data: siblingsResp } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets',
        {
            params: {
                path: { orgCode: org },
                query: parentUid
                    ? ({ parent_uid: parentUid, limit: 100 } as unknown as Record<string, unknown>)
                    : ({ limit: 100 } as unknown as Record<string, unknown>),
            },
        },
        { enabled: !!parentUid },
    )

    const { data: childrenResp } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets',
        {
            params: {
                path: { orgCode: org },
                query: { parent_uid: defaultAssetUid, limit: 100 } as unknown as Record<string, unknown>,
            },
        },
    )

    const candidatesAssets = useMemo(() => {
        const acc: { uid: string; name: string }[] = []
        const pushIfNew = (a: { uid?: string; name?: string }) => {
            if (a.uid && !acc.find(x => x.uid === a.uid)) {
                acc.push({ uid: a.uid, name: a.name || a.uid })
            }
        }
        if (asset?.uid) pushIfNew(asset)
        for (const a of (siblingsResp?.data ?? [])) pushIfNew(a)
        for (const a of (childrenResp?.data ?? [])) pushIfNew(a)
        return acc
    }, [asset, siblingsResp, childrenResp])

    const [choices, setChoices] = useState<Choice[]>(() =>
        candidates.map((c, i) => ({
            candidate: c,
            assetUid: i === 0 ? defaultAssetUid : '',
            skip: false,
        })),
    )

    useEffect(() => {
        // If the default asset isn't already in the picker list, ensure it shows up
    }, [candidatesAssets])

    const allValid = choices.every(c => c.skip || (c.assetUid && c.assetUid.length > 0))

    const submit = async () => {
        setSubmitting(true)
        try {
            await onConfirm(choices)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open onOpenChange={open => { if (!open) onCancel() }}>
            <DialogContent className="max-w-3xl" data-testid="plan-wizard">
                <DialogHeader>
                    <DialogTitle>{t('plan.wizard.title', 'Detected plans')}</DialogTitle>
                </DialogHeader>

                <p className="text-sm text-muted-foreground">
                    {t('plan.wizard.subtitle', 'We detected {{count}} possible plans in this document. Assign each one to an asset, or skip.', { count: candidates.length })}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-auto">
                    {choices.map((choice, i) => {
                        const c = choice.candidate
                        const confColor = c.confidence === 'high'
                            ? 'bg-green-500/15 text-green-700 dark:text-green-300'
                            : c.confidence === 'medium'
                                ? 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300'
                                : 'bg-red-500/15 text-red-700 dark:text-red-300'
                        return (
                            <div
                                key={`${c.page}-${i}`}
                                className={`border border-border rounded-md p-3 space-y-2 ${choice.skip ? 'opacity-50' : ''}`}
                                data-testid={`plan-wizard-card-${i}`}
                            >
                                <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                                    <AppIcon tone="muted" path={mdiFileChart} size="xl" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">
                                        {c.suggested_title || t('plan.wizard.page_label', 'Page {{page}}', { page: c.page })}
                                    </span>
                                    <Badge variant="outline" className={`text-xs ${confColor}`}>{c.confidence}</Badge>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {t('plan.wizard.page_label', 'Page {{page}}', { page: c.page })}
                                </div>

                                <div className="space-y-1">
                                    <div className="text-xs text-muted-foreground">{t('plan.wizard.assign_to', 'Assign to')}</div>
                                    <Select
                                        value={choice.assetUid || ''}
                                        onValueChange={v => {
                                            setChoices(prev => prev.map((x, idx) => (idx === i ? { ...x, assetUid: v } : x)))
                                        }}
                                        disabled={choice.skip}
                                    >
                                        <SelectTrigger data-testid={`plan-wizard-asset-${i}`}>
                                            <SelectValue placeholder={t('plan.wizard.pick_asset', 'Pick an asset…')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {candidatesAssets.length === 0 && (
                                                <SelectItem value={defaultAssetUid}>
                                                    {asset?.name || t('plan.wizard.this_asset', 'This asset')}
                                                </SelectItem>
                                            )}
                                            {candidatesAssets.map(a => (
                                                <SelectItem key={a.uid} value={a.uid}>{a.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                    <Checkbox
                                        checked={choice.skip}
                                        onCheckedChange={(checked: boolean | 'indeterminate') => {
                                            setChoices(prev => prev.map((x, idx) =>
                                                (idx === i ? { ...x, skip: !!checked } : x)
                                            ))
                                        }}
                                        data-testid={`plan-wizard-skip-${i}`}
                                    />
                                    <span>{t('plan.wizard.skip', 'Skip')}</span>
                                </label>
                            </div>
                        )
                    })}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onCancel}
                        disabled={submitting}
                        data-testid="plan-wizard-cancel"
                    >
                        {t('plan.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!allValid || submitting}
                        data-testid="plan-wizard-create"
                    >
                        <AppIcon path={mdiFileImage} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">
                            {submitting ? t('plan.wizard.creating', 'Creating…') : t('plan.wizard.create', 'Create plans')}
                        </span>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
